import { Injectable } from 'framework/di';

import { AppService } from '../../app/app.service';
import { CartAPI } from '../cart.api';
import { CartDrawerRecommendationConstants } from './constants';
import { CartDrawerRecommendationService } from './service';

/**
 * Carousel state for tracking slide position and configuration.
 * Used internally by the controller to manage the native CSS/JS carousel.
 */
interface CarouselState {
  currentIndex: number;
  totalSlides: number;
  slidesPerView: number;
  slideWidth: number;
  containerElement: HTMLElement | null;
  sliderElement: HTMLElement | null;
}

/**
 * Drag state for tracking touch/mouse drag interactions
 * in the native carousel.
 */
interface DragState {
  isDragging: boolean;
  startX: number;
  currentX: number;
  startTranslateX: number;
}

/**
 * Controller for managing recommendation widget interactions within the cart drawer.
 *
 * NOTE: This controller does NOT extend RecommendationWidgetController to avoid circular
 * dependency (RecommendationModule imports CartModule). Instead, it implements its own
 * `formatPrice()` method using the same logic as the base controller.
 *
 * This controller handles user interactions with recommendation products that are
 * rendered by the backend cart template. The template renders the recommendation
 * widget using data from CartService.getCartDrawerRecommendationData().
 *
 * ## Key Responsibilities
 * - Handles "Add to Cart" button clicks from recommendation products
 * - Manages native CSS/JS carousel (not Slick) for recommendation products
 * - Dispatches cart updated events after adding products
 * - Manages event listeners for cart drawer lifecycle
 *
 * ## Extension Points
 * - `getAddToCartButtonSelector()` - Customize the Add to Cart button selector
 * - `getRecommendationContainerSelector()` - Customize the recommendation container selector
 * - `handleAddToCart()` - Customize add to cart behavior
 * - `afterCartDrawerAddToCart()` - Add custom post-add-to-cart logic
 * - `getSlidesPerView()` - Customize number of slides per view based on cart style
 * - `getHighlightDuration()` - Customize success/error message display duration
 * - `getErrorMessage()` - Customize error message extraction for shop-specific errors
 * - `formatPrice()` - Customize price formatting for variant price updates
 * - `getAddingButtonText()` / `getAddedButtonText()` / `getFailedButtonText()` / `getDefaultAddButtonText()` / `getSuccessMessageText()` - Customize button and message strings for localization
 *
 * @example
 * Extend to customize add to cart behavior:
 * ```javascript
 * window.boostWidgetIntegration.extend('CartDrawerRecommendationController', (Base) => {
 *   return class CustomCartDrawerRec extends Base {
 *     async handleAddToCart(productId, variantId) {
 *       // Custom logic before adding to cart
 *       await super.handleAddToCart(productId, variantId);
 *       // Custom logic after adding to cart
 *     }
 *   };
 * });
 * ```
 */
@Injectable
export class CartDrawerRecommendationController {
  protected isInitialized = false;
  protected boundClickHandler: ((event: Event) => void) | null = null;
  protected boundChangeHandler: ((event: Event) => void) | null = null;
  protected boundDragStartHandler: ((event: Event) => void) | null = null;
  protected boundResizeHandler: (() => void) | null = null;
  protected nativeCarouselState: CarouselState | null = null;
  protected dragState: DragState = {
    isDragging: false,
    startX: 0,
    currentX: 0,
    startTranslateX: 0,
  };

  // Event handlers for cleanup
  protected readonly cartOpenedHandler = (): void => {
    this.setupClickHandlers();
    this.initNativeCarousel();
  };

  protected readonly cartClosedHandler = (): void => {
    this.removeClickHandlers();
    this.destroyNativeCarousel();
  };

  constructor(
    protected appService: AppService,
    protected cartAPI: CartAPI,
    protected cartDrawerRecommendationService: CartDrawerRecommendationService,
    protected constants: CartDrawerRecommendationConstants
  ) {
    this.setupCartDrawerEventListeners();
  }

  /**
   * Sets up event listeners for cart drawer open/close events.
   */
  protected setupCartDrawerEventListeners(): void {
    window.addEventListener('boost-sd-cart-drawer-opened', this.cartOpenedHandler);
    window.addEventListener('boost-sd-cart-drawer-closed', this.cartClosedHandler);
  }

  /**
   * Removes cart drawer event listeners.
   */
  protected removeCartDrawerEventListeners(): void {
    window.removeEventListener('boost-sd-cart-drawer-opened', this.cartOpenedHandler);
    window.removeEventListener('boost-sd-cart-drawer-closed', this.cartClosedHandler);
  }

  /**
   * Get the CSS selector for Add to Cart buttons in cart drawer recommendations.
   * Override this method to use a different button selector.
   *
   * @returns CSS selector string for the Add to Cart buttons
   */
  protected getAddToCartButtonSelector(): string {
    return this.constants.SELECTORS.ADD_TO_CART_BUTTON;
  }

  /**
   * Get the CSS selector for the recommendation container in cart drawer.
   * Override this method to use a different container selector.
   *
   * @returns CSS selector string for the recommendation container
   */
  protected getRecommendationContainerSelector(): string {
    return this.constants.SELECTORS.RECOMMENDATION_CONTAINER;
  }

  /**
   * Get the CSS selector for the cart drawer modal.
   * Override this method if your theme uses a different cart drawer structure.
   *
   * @returns CSS selector string for the cart drawer modal
   */
  protected getCartDrawerSelector(): string {
    return this.constants.SELECTORS.CART_MODAL;
  }

  /**
   * Sets up click handlers for Add to Cart buttons in recommendations.
   * Called when the cart drawer is opened.
   */
  protected setupClickHandlers(): void {
    const cartDrawer = document.querySelector(this.getCartDrawerSelector());
    if (!cartDrawer) {
      return;
    }

    // Remove existing handler if any
    this.removeClickHandlers();

    // Create and store the click handler
    this.boundClickHandler = (event: Event): void => {
      const target = event.target as HTMLElement;
      const addButton = target.closest(this.getAddToCartButtonSelector()) as HTMLElement;

      if (addButton) {
        event.preventDefault();
        event.stopPropagation();

        // Get the product item container
        const productItem = addButton.closest(this.constants.SELECTORS.PRODUCT_ITEM) as HTMLElement;
        if (!productItem) return;

        const productId = productItem.getAttribute('data-product-id');
        // Get variant ID from product item (updated by variant select) or fallback to button
        const variantId = productItem.getAttribute('data-variant-id');

        if (productId && variantId) {
          this.handleAddToCart(productId, variantId, addButton);
        }
      }
    };

    // Add click listener to cart drawer for event delegation
    cartDrawer.addEventListener('click', this.boundClickHandler);

    // Setup variant select change handlers
    this.setupVariantSelectHandlers(cartDrawer as HTMLElement);

    this.isInitialized = true;

    this.appService.logger.debug('Cart drawer recommendation click handlers initialized');
  }

  /**
   * Setup change handlers for variant select dropdowns.
   * Uses event delegation on the cart drawer container to avoid memory leaks.
   * Updates the product item's data-variant-id when variant selection changes.
   *
   * @param cartDrawer - The cart drawer element
   */
  protected setupVariantSelectHandlers(cartDrawer: HTMLElement): void {
    // Remove existing handler if any (prevents duplicates)
    this.removeVariantSelectHandlers();

    // Create delegated change handler
    this.boundChangeHandler = (event: Event): void => {
      const target = event.target as HTMLElement;

      // Check if the change event came from a variant select
      if (!target.matches(this.getVariantSelectSelector())) {
        return;
      }

      const selectElement = target as HTMLSelectElement;
      const selectedOption = selectElement.options[selectElement.selectedIndex];
      const variantId = selectedOption.getAttribute('data-variant-id');
      const productItem = selectElement.closest(
        this.constants.SELECTORS.PRODUCT_ITEM
      ) as HTMLElement;

      if (productItem && variantId) {
        // Update product item's variant ID
        productItem.setAttribute('data-variant-id', variantId);

        // Optionally update product image if variant has an image
        const variantImage = selectedOption.getAttribute('data-variant-image');
        if (variantImage) {
          this.updateProductImage(productItem, variantImage);
        }

        // Update price if variant has different price
        const variantPrice = selectedOption.getAttribute('data-variant-price');
        if (variantPrice) {
          this.updateProductPrice(productItem, variantPrice);
        }
      }
    };

    // Add delegated change listener to cart drawer
    cartDrawer.addEventListener('change', this.boundChangeHandler);
  }

  /**
   * Removes the delegated change handler for variant selects.
   * Called when the cart drawer is closed.
   */
  protected removeVariantSelectHandlers(): void {
    if (this.boundChangeHandler) {
      const cartDrawer = document.querySelector(this.getCartDrawerSelector());
      if (cartDrawer) {
        cartDrawer.removeEventListener('change', this.boundChangeHandler);
      }
      this.boundChangeHandler = null;
    }
  }

  /**
   * Get the CSS selector for variant select dropdowns.
   *
   * @returns CSS selector string for variant select elements
   */
  protected getVariantSelectSelector(): string {
    return this.constants.SELECTORS.VARIANT_SELECT;
  }

  /**
   * Update the product image when variant changes.
   * Override to customize image update behavior.
   *
   * @param productItem - The product item container element
   * @param imageUrl - The new image URL
   *
   * @example
   * Override to add lazy loading or custom image transformations:
   * ```typescript
   * protected updateProductImage(productItem: HTMLElement, imageUrl: string) {
   *   const img = productItem.querySelector('.boost-sd__rcu-item-image img') as HTMLImageElement;
   *   if (img) {
   *     img.loading = 'lazy';
   *     img.src = `${imageUrl}&width=${this.getImageWidth()}&quality=80`;
   *   }
   * }
   * ```
   */
  protected updateProductImage(productItem: HTMLElement, imageUrl: string): void {
    const imageElement = productItem.querySelector(
      this.constants.SELECTORS.PRODUCT_IMAGE
    ) as HTMLImageElement;
    if (imageElement) {
      const separator = imageUrl.includes('?') ? '&' : '?';
      const imageWidth = this.getImageWidth();
      imageElement.src = `${imageUrl}${separator}width=${imageWidth}`;
    }
  }

  /**
   * Get the image width for recommendation product images.
   * Checks widget design settings first, then falls back to default.
   *
   * Override this method to customize image width calculation.
   *
   * @returns Image width in pixels
   *
   * @example
   * Override to use a different image width for retina displays:
   * ```typescript
   * protected getImageWidth() {
   *   return window.devicePixelRatio > 1 ? 600 : 300;
   * }
   * ```
   */
  protected getImageWidth(): number {
    const recommendationContainer = document.querySelector(
      this.getRecommendationContainerSelector()
    );
    const widgetDesignSettings = this.getWidgetDesignSettings(recommendationContainer);

    const configuredWidth = widgetDesignSettings?.imageWidth;
    if (typeof configuredWidth === 'number' && configuredWidth > 0) {
      return configuredWidth;
    }

    return this.constants.DEFAULTS.IMAGE_WIDTH;
  }

  /**
   * Update the product price when variant changes.
   * Override to customize price update behavior.
   *
   * Uses the local `formatPrice()` method for consistent price formatting
   * across all recommendation widgets, including currency code display
   * based on cart settings.
   *
   * @param productItem - The product item container element
   * @param price - The price value (may be formatted string or raw number string)
   *
   * @example
   * Override to show compare-at price alongside variant price:
   * ```typescript
   * protected updateProductPrice(productItem: HTMLElement, price: string) {
   *   super.updateProductPrice(productItem, price);
   *   const comparePrice = productItem.querySelector('.boost-sd__rcu-item-compare-price');
   *   if (comparePrice) {
   *     comparePrice.style.display = 'inline';
   *   }
   * }
   * ```
   */
  protected updateProductPrice(productItem: HTMLElement, price: string): void {
    const priceElement = productItem.querySelector(this.constants.SELECTORS.PRODUCT_PRICE);
    if (priceElement) {
      // Get showCurrencyCodes setting from cart settings
      const showCurrencyCodes = this.appService.themeSettings.cart?.showCurrencyCodes ?? false;

      // Check if price looks like a raw number (only digits, dots, commas)
      // If so, format it using formatPrice(); otherwise use as-is
      const formattedPrice = this.formatPriceIfNeeded(price, showCurrencyCodes);
      priceElement.textContent = formattedPrice;
    }
  }

  /**
   * Formats a price value according to shop settings and theme configuration.
   *
   * This method implements the same logic as RecommendationWidgetController.formatPrice()
   * but is duplicated here to avoid circular dependency issues.
   *
   * @param price - The price value to format (in store currency unit, NOT cents)
   * @param showCurrencyCodes - Whether to display currency codes (e.g., "USD") alongside the price
   * @returns Formatted price string with currency symbol and shop-specific styling
   *
   * @example
   * Override to add custom price formatting for a specific shop:
   * ```javascript
   * window.boostWidgetIntegration.extend('CartDrawerRecommendationController', (Base) => {
   *   return class extends Base {
   *     formatPrice(price, showCurrencyCodes) {
   *       // Apply shop-specific rounding before formatting
   *       const roundedPrice = Math.ceil(price);
   *       return super.formatPrice(roundedPrice, showCurrencyCodes);
   *     }
   *   };
   * });
   * ```
   */
  public formatPrice(price: number, showCurrencyCodes = false): string {
    const taeConfig = this.appService.TAEAppConfig;

    const priceSettings =
      taeConfig.templateSettings?.themeSettings?.productItems?.productInfo?.elements?.price || {};

    const showCentAsSuperscript = priceSettings.showCentAsSuperscript;
    const removePriceDecimal = Boolean(
      (priceSettings as Record<string, unknown>).removePriceDecimal
    );

    // Use money_format_with_currency if showCurrencyCodes, otherwise use money_format
    const formatString = showCurrencyCodes
      ? taeConfig.shop?.money_format_with_currency || taeConfig.shop?.money_format || '${{amount}}'
      : taeConfig.shop?.money_format || '${{amount}}';

    const formattedResult = this.appService.formatCurrency(price, {
      format: formatString,
      showCentAsSuperscript: showCentAsSuperscript,
      removePriceDecimal: removePriceDecimal,
      showCurrencyCodes: showCurrencyCodes,
    });

    return formattedResult || '';
  }

  /**
   * Format price if it appears to be a raw number string.
   * If the price already contains currency symbols or non-numeric characters,
   * it's considered already formatted and returned as-is.
   *
   * Uses the local `formatPrice()` method for consistent formatting.
   *
   * @param price - Price string that may be raw number or formatted
   * @param showCurrencyCodes - Whether to display currency codes (e.g., "USD") alongside the price
   * @returns Formatted price string
   */
  protected formatPriceIfNeeded(price: string, showCurrencyCodes = false): string {
    // Check if price is a raw number (digits, dots, commas only)
    // Patterns like "100", "19.99", "1,999.00" are raw numbers
    // Patterns like "$19.99", "100đ", "€19.99" are already formatted
    const isRawNumber = /^[\d,.\s]+$/.test(price.trim());

    if (isRawNumber) {
      // Parse as number and format using formatPrice()
      const numericPrice = parseFloat(price.replace(/[,\s]/g, ''));
      if (!isNaN(numericPrice)) {
        return this.formatPrice(numericPrice, showCurrencyCodes);
      }
    }

    // Already formatted or couldn't parse - return as-is
    return price;
  }

  /**
   * Removes click and change handlers from the cart drawer.
   * Called when the cart drawer is closed.
   */
  protected removeClickHandlers(): void {
    if (this.boundClickHandler) {
      const cartDrawer = document.querySelector(this.getCartDrawerSelector());
      if (cartDrawer) {
        cartDrawer.removeEventListener('click', this.boundClickHandler);
      }
      this.boundClickHandler = null;
    }

    // Also remove variant select change handlers
    this.removeVariantSelectHandlers();

    this.isInitialized = false;
  }

  /**
   * Get the button text displayed while adding a product to cart.
   * Override this method to customize button text for localization or shop-specific messaging.
   *
   * @returns Button text string shown during add-to-cart operation
   *
   * @example
   * Override for French localization:
   * ```typescript
   * protected getAddingButtonText() {
   *   return 'Ajout en cours...';
   * }
   * ```
   */
  protected getAddingButtonText(): string {
    return 'Adding...';
  }

  /**
   * Get the button text displayed after successfully adding a product to cart.
   * Override this method to customize success button text for localization or shop-specific messaging.
   *
   * @returns Button text string shown after successful add-to-cart
   *
   * @example
   * Override for Spanish localization:
   * ```typescript
   * protected getAddedButtonText() {
   *   return '¡Añadido!';
   * }
   * ```
   */
  protected getAddedButtonText(): string {
    return 'Added!';
  }

  /**
   * Get the button text displayed when adding a product to cart fails.
   * Override this method to customize error button text for localization or shop-specific messaging.
   *
   * @returns Button text string shown after failed add-to-cart attempt
   *
   * @example
   * Override for German localization:
   * ```typescript
   * protected getFailedButtonText() {
   *   return 'Fehler';
   * }
   * ```
   */
  protected getFailedButtonText(): string {
    return 'Failed';
  }

  /**
   * Get the default button text for the Add to Cart button.
   * Override this method to customize default button text for localization or shop-specific messaging.
   *
   * @returns Default button text string
   *
   * @example
   * Override for Italian localization:
   * ```typescript
   * protected getDefaultAddButtonText() {
   *   return 'Aggiungi';
   * }
   * ```
   */
  protected getDefaultAddButtonText(): string {
    return 'Add';
  }

  /**
   * Get the success message text displayed after adding a product to cart.
   * Override this method to customize success message for localization or shop-specific messaging.
   *
   * @returns Success message text string
   *
   * @example
   * Override for Japanese localization:
   * ```typescript
   * protected getSuccessMessageText() {
   *   return 'カートに追加されました！';
   * }
   * ```
   */
  protected getSuccessMessageText(): string {
    return 'Added to cart!';
  }

  /**
   * Get highlight duration in milliseconds for success/error states.
   * Override to adjust how long success and error messages remain visible.
   *
   * @returns Duration in milliseconds
   *
   * @example
   * Override to increase message display duration:
   * ```typescript
   * protected getHighlightDuration() {
   *   return 3000; // Show messages for 3 seconds
   * }
   * ```
   */
  protected getHighlightDuration(): number {
    return 1000;
  }

  /**
   * Get the delay in milliseconds before resetting the Add to Cart button text after success or error.
   * Override to adjust how long the success/error button state remains visible before returning to default.
   *
   * @returns Delay duration in milliseconds
   *
   * @example
   * Override to show button state longer:
   * ```typescript
   * protected getButtonResetDelay() {
   *   return 2500; // Keep success/error state for 2.5 seconds
   * }
   * ```
   */
  protected getButtonResetDelay(): number {
    return 1500;
  }

  /**
   * Get the quantity to add when clicking the Add to Cart button.
   * Override to implement quantity pickers or per-product custom quantities.
   *
   * @param productItem - The product item element (for reading data attributes)
   * @returns Quantity to add (default: 1)
   *
   * @example
   * Override to read quantity from a number input on the product card:
   * ```javascript
   * window.boostWidgetIntegration.extend('CartDrawerRecommendationController', (Base) => {
   *   return class extends Base {
   *     getAddToCartQuantity(productItem) {
   *       const input = productItem.querySelector('input[type="number"]');
   *       return input ? parseInt(input.value, 10) || 1 : 1;
   *     }
   *   };
   * });
   * ```
   */
  protected getAddToCartQuantity(productItem: HTMLElement): number {
    const quantityAttr = productItem.getAttribute('data-quantity');
    if (quantityAttr) {
      const parsed = parseInt(quantityAttr, 10);
      if (!isNaN(parsed) && parsed > 0) {
        return parsed;
      }
    }

    return 1;
  }

  /**
   * Handles adding a product to cart from recommendation widget.
   *
   * Optimization: Does NOT re-fetch recommendation API after add to cart.
   * - If product has 0-1 variants: hides the product from widget (DOM only)
   * - If all products are hidden: re-fetches API to get new recommendations
   * - If product has 2+ variants: keeps it visible for user to add other variants
   *
   * @param productId - The product ID to add
   * @param variantId - The variant ID to add
   * @param buttonElement - The clicked button element (for loading state)
   *
   * @example
   * Override to add custom validation or quantity logic before adding to cart:
   * ```javascript
   * window.boostWidgetIntegration.extend('CartDrawerRecommendationController', (Base) => {
   *   return class extends Base {
   *     async handleAddToCart(productId, variantId, buttonElement) {
   *       // Example: add custom quantity or properties
   *       const quantity = this.getCustomQuantity(productId);
   *       if (quantity > 0) {
   *         await super.handleAddToCart(productId, variantId, buttonElement);
   *       }
   *     }
   *   };
   * });
   * ```
   */
  protected async handleAddToCart(
    productId: string,
    variantId: string,
    buttonElement: HTMLElement
  ): Promise<void> {
    const productItem = buttonElement.closest(this.constants.SELECTORS.PRODUCT_ITEM) as HTMLElement;
    const originalText = buttonElement.textContent;

    // Prevent double-clicks
    if (buttonElement.hasAttribute('disabled')) {
      return;
    }

    try {
      // Set loading state
      buttonElement.setAttribute('disabled', 'true');
      buttonElement.classList.add(this.constants.CSS_CLASSES.BUTTON_LOADING);
      buttonElement.textContent = this.getAddingButtonText();

      // Clear any previous error message
      this.clearErrorMessage(productItem);

      // Add to cart via CartAPI (throws on failure, so reaching past this line means success)
      await this.cartAPI.add(variantId, this.getAddToCartQuantity(productItem));

      // Success state
      buttonElement.classList.remove(this.constants.CSS_CLASSES.BUTTON_LOADING);
      buttonElement.classList.add(this.constants.CSS_CLASSES.BUTTON_SUCCESS);
      buttonElement.textContent = this.getAddedButtonText();

      // Call after add hook
      await this.afterCartDrawerAddToCart(productId, variantId);

      // Dispatch event to refresh cart drawer (re-renders cart items only, not recommendation)
      window.dispatchEvent(new CustomEvent('boost-sd-re-render-cart'));

      // Show success message in the recommendation widget
      this.showSuccessMessage(productItem);

      // Handle product visibility based on variant count (optimization: no API call)
      const variantCount = this.getProductVariantCount(productItem);
      if (variantCount <= 1) {
        // Hide product from widget (single/no variant - nothing else to add)
        this.hideProductFromWidget(productItem);

        // Check if all products are now hidden - if so, re-fetch API
        if (this.areAllProductsHidden()) {
          this.appService.logger.debug(
            'All RCU products hidden after add to cart, re-fetching recommendations'
          );
          await this.refetchAndRenderRecommendations();
        }
      }
      // If product has 2+ variants, keep it visible (user may want other variants)

      // Reset button after a delay (only if product still visible)
      setTimeout(() => {
        if (productItem.style.display !== 'none') {
          buttonElement.classList.remove(this.constants.CSS_CLASSES.BUTTON_SUCCESS);
          buttonElement.textContent = originalText;
          buttonElement.removeAttribute('disabled');
        }
      }, this.getButtonResetDelay());

      this.appService.logger.info('Cart drawer recommendation: Product added to cart', {
        productId,
        variantId,
        variantCount,
        productHidden: variantCount <= 1,
      });
    } catch (error) {
      // Reset button on error
      buttonElement.classList.remove(this.constants.CSS_CLASSES.BUTTON_LOADING);
      buttonElement.classList.add(this.constants.CSS_CLASSES.BUTTON_ERROR);
      buttonElement.textContent = this.getFailedButtonText();

      // Show error message on the recommendation item
      const errorMessage = this.getErrorMessage(error);
      this.showErrorMessage(productItem, errorMessage);

      // Reset button after a delay
      setTimeout(() => {
        buttonElement.classList.remove(this.constants.CSS_CLASSES.BUTTON_ERROR);
        buttonElement.textContent = originalText || this.getDefaultAddButtonText();
        buttonElement.removeAttribute('disabled');
      }, this.getButtonResetDelay());

      this.appService.logger.error('Cart drawer recommendation: Failed to add product', error);
    }
  }

  /**
   * Get the number of variants for a product from its DOM element.
   * Checks data attribute or variant selector options.
   *
   * @param productItem - The product item element
   * @returns Number of variants (0 if no variants, 1+ otherwise)
   */
  protected getProductVariantCount(productItem: HTMLElement): number {
    // First, try to get from data attribute (set during template render)
    const variantCountAttr = productItem.getAttribute('data-variant-count');
    if (variantCountAttr) {
      return parseInt(variantCountAttr, 10) || 0;
    }

    // Fallback: count variant options in the variant selector
    const variantSelector = productItem.querySelector(this.constants.SELECTORS.VARIANT_SELECT);
    if (variantSelector) {
      const options = variantSelector.querySelectorAll('option');
      return options.length;
    }

    // No variant selector = single/default variant
    return 1;
  }

  /**
   * Hide a product from the recommendation widget.
   * Uses CSS display:none for immediate visual feedback.
   *
   * @param productItem - The product item element to hide
   *
   * @example
   * Override to use a custom animation or delay before hiding:
   * ```javascript
   * window.boostWidgetIntegration.extend('CartDrawerRecommendationController', (Base) => {
   *   return class extends Base {
   *     hideProductFromWidget(productItem) {
   *       productItem.classList.add('rcu-item--sold');
   *       setTimeout(() => super.hideProductFromWidget(productItem), 500);
   *     }
   *   };
   * });
   * ```
   */
  protected hideProductFromWidget(productItem: HTMLElement): void {
    if (!productItem) return;

    // Mark as hidden immediately so areAllProductsHidden() can detect it
    // before the fade-out animation completes
    productItem.dataset.rcuHidden = 'true';

    // Add fade-out animation
    productItem.style.transition = 'opacity 0.3s ease-out';
    productItem.style.opacity = '0';

    // After animation, hide completely
    setTimeout(() => {
      productItem.style.display = 'none';

      // Update carousel if active
      if (this.nativeCarouselState) {
        this.updateCarouselAfterProductRemoval();
      }
    }, 300);

    this.appService.logger.debug('Hiding product from RCU widget', {
      productId: productItem.getAttribute('data-product-id'),
    });
  }

  /**
   * Check if all products in the recommendation widget are hidden.
   * Checks both the synchronous `data-rcu-hidden` attribute (set immediately
   * by {@link hideProductFromWidget}) and `display:none` (set after fade animation).
   *
   * @returns True if all products are hidden or pending hide
   */
  protected areAllProductsHidden(): boolean {
    const container = document.querySelector(this.getRecommendationContainerSelector());
    if (!container) return true;

    const allProducts = container.querySelectorAll(this.constants.SELECTORS.PRODUCT_ITEM);
    if (allProducts.length === 0) return true;

    // Check if any product is still visible
    // A product is considered hidden if it has display:none OR the data-rcu-hidden marker
    // (the marker is set synchronously before the fade-out animation completes)
    for (const product of allProducts) {
      const element = product as HTMLElement;
      if (element.style.display !== 'none' && !element.dataset.rcuHidden) {
        return false;
      }
    }

    return true;
  }

  /**
   * Re-fetch recommendation data and re-render the widget.
   * Called when all products have been hidden after add-to-cart actions.
   *
   * @remarks
   * This method adds a loading state class to the container during the fetch,
   * and hides the entire widget if no more recommendations are available.
   *
   * @example
   * Override to add custom behavior when recommendations are refreshed:
   * ```typescript
   * protected async refetchAndRenderRecommendations() {
   *   // Show a custom loading indicator
   *   this.showCustomLoader();
   *   await super.refetchAndRenderRecommendations();
   *   this.hideCustomLoader();
   * }
   * ```
   */
  protected async refetchAndRenderRecommendations(): Promise<void> {
    try {
      const container = document.querySelector(this.getRecommendationContainerSelector());
      if (!container) return;

      // Show loading state
      container.classList.add(this.constants.CSS_CLASSES.RECOMMENDATION_LOADING);

      // Get cart settings for currency display
      const showCurrencyCodes = this.appService.themeSettings.cart?.showCurrencyCodes ?? false;

      // Re-fetch recommendation data
      const recommendationData =
        await this.cartDrawerRecommendationService.getCartDrawerRecommendationData(
          showCurrencyCodes
        );

      if (recommendationData && recommendationData.products.length > 0) {
        // Re-render the recommendation widget
        await this.renderRecommendationWidget(container as HTMLElement, recommendationData);

        // Re-initialize carousel for new products
        // (destroyNativeCarousel is called internally by initNativeCarousel)
        this.initNativeCarousel();
      } else {
        // No more recommendations - hide the entire widget
        this.hideRecommendationWidget(container as HTMLElement);
      }

      container.classList.remove(this.constants.CSS_CLASSES.RECOMMENDATION_LOADING);
    } catch (error) {
      this.appService.logger.error('Failed to re-fetch recommendations', error);
    }
  }

  /**
   * Render the recommendation widget with new data.
   * Override this method to customize rendering.
   *
   * Returns a Promise that resolves when the DOM update is complete.
   * The consumer listening to 'boost-sd-rcu-refetch' should dispatch
   * 'boost-sd-rcu-render-complete' after finishing the DOM update.
   *
   * @remarks
   * A fallback timeout of 1000ms is used in case the completion event
   * is never fired. If the consumer needs more time, override this method
   * to adjust the timeout.
   *
   * @param container - The container element to render into
   * @param data - The recommendation data from CartDrawerRecommendationService
   * @returns Promise that resolves when rendering is complete or timeout is reached
   *
   * @example
   * Override to add a custom loading spinner during re-render:
   * ```typescript
   * protected async renderRecommendationWidget(container, data) {
   *   container.innerHTML = '<div class="custom-spinner"></div>';
   *   await super.renderRecommendationWidget(container, data);
   *   // Custom post-render logic
   * }
   * ```
   */
  protected async renderRecommendationWidget(
    container: HTMLElement,
    data: Awaited<
      ReturnType<CartDrawerRecommendationService['getCartDrawerRecommendationData']>
    > & { products: Array<Record<string, unknown>> }
  ): Promise<void> {
    return new Promise<void>((resolve) => {
      // Fallback timeout in case the completion event is never fired
      // This prevents indefinite waiting if the consumer doesn't dispatch the event
      const fallbackTimeoutId = setTimeout(() => {
        window.removeEventListener('boost-sd-rcu-render-complete', handleRenderComplete);
        this.appService.logger.warn(
          'RCU render complete event not received, proceeding with carousel init'
        );
        resolve();
      }, 1000);

      // Listen for completion event from the consumer
      const handleRenderComplete = (): void => {
        clearTimeout(fallbackTimeoutId);
        window.removeEventListener('boost-sd-rcu-render-complete', handleRenderComplete);
        resolve();
      };

      window.addEventListener('boost-sd-rcu-render-complete', handleRenderComplete);

      // Dispatch the refetch event for CartService to handle
      window.dispatchEvent(
        new CustomEvent('boost-sd-rcu-refetch', {
          detail: { recommendationData: data },
        })
      );
    });
  }

  /**
   * Hide the entire recommendation widget when no products available.
   *
   * @param container - The recommendation container element
   */
  protected hideRecommendationWidget(container: HTMLElement): void {
    if (!container) return;

    container.style.transition = 'opacity 0.3s ease-out';
    container.style.opacity = '0';

    setTimeout(() => {
      container.style.display = 'none';
    }, 300);

    this.appService.logger.debug('Hiding RCU widget - no more recommendations');
  }

  /**
   * Update carousel state after a product has been removed.
   * Recalculates slide positions and updates navigation.
   */
  protected updateCarouselAfterProductRemoval(): void {
    if (!this.nativeCarouselState) return;

    const container = this.nativeCarouselState.containerElement;
    if (!container) return;

    // Count visible products
    const visibleProducts = container.querySelectorAll(
      `${this.constants.SELECTORS.PRODUCT_ITEM}:not([style*="display: none"])`
    );
    const newTotalSlides = visibleProducts.length;

    // Update carousel state
    this.nativeCarouselState.totalSlides = newTotalSlides;

    // Ensure current index is still valid
    const maxIndex = Math.max(0, newTotalSlides - this.nativeCarouselState.slidesPerView);
    if (this.nativeCarouselState.currentIndex > maxIndex) {
      this.nativeCarouselState.currentIndex = maxIndex;
      this.updateNativeCarouselPosition();
    }

    // Update navigation buttons visibility
    this.updateNativeCarouselButtons();
  }

  /**
   * Show a success message in the recommendation widget after adding to cart.
   *
   * @param productItem - The recommendation product item element
   */
  protected showSuccessMessage(productItem: HTMLElement | null): void {
    if (!productItem) return;

    // Remove any existing success/error message
    this.clearSuccessMessage(productItem);
    this.clearErrorMessage(productItem);

    // Create success message element
    const successElement = document.createElement('div');
    successElement.className = this.constants.CSS_CLASSES.SUCCESS_MESSAGE;
    successElement.style.cssText = `
      color: #28a745;
      font-size: 12px;
      margin-top: 4px;
      padding: 4px 8px;
      font-weight: 500;
      animation: fadeIn 0.3s ease-in-out;
    `;
    successElement.textContent = this.getSuccessMessageText();

    // Append to product item info section
    const infoSection = productItem.querySelector(this.constants.SELECTORS.PRODUCT_INFO);
    if (infoSection) {
      infoSection.appendChild(successElement);
    } else {
      productItem.appendChild(successElement);
    }

    // Auto-remove success message after duration
    setTimeout(() => {
      this.clearSuccessMessage(productItem);
    }, this.getHighlightDuration() * 2); // Keep success message visible longer
  }

  /**
   * Clear success message from a recommendation item.
   *
   * @param productItem - The recommendation product item element
   */
  protected clearSuccessMessage(productItem: HTMLElement | null): void {
    if (!productItem) return;

    const successElement = productItem.querySelector(this.constants.SELECTORS.SUCCESS_MESSAGE);
    if (successElement) {
      successElement.remove();
    }
  }

  /**
   * Show an error message on a recommendation item.
   *
   * @param productItem - The recommendation product item element
   * @param message - The error message to display
   */
  protected showErrorMessage(productItem: HTMLElement | null, message: string): void {
    if (!productItem) return;

    // Remove any existing error message
    this.clearErrorMessage(productItem);

    // Create error message element
    const errorElement = document.createElement('div');
    errorElement.className = this.constants.CSS_CLASSES.ERROR_MESSAGE;
    errorElement.style.cssText = `
      color: #dc3545;
      font-size: 12px;
      margin-top: 4px;
      padding: 4px 8px;
      background-color: rgba(220, 53, 69, 0.1);
      border-radius: 4px;
      animation: fadeIn 0.3s ease-in-out;
    `;
    errorElement.textContent = message;

    // Append to product item info section
    const infoSection = productItem.querySelector(this.constants.SELECTORS.PRODUCT_INFO);
    if (infoSection) {
      infoSection.appendChild(errorElement);
    } else {
      productItem.appendChild(errorElement);
    }

    // Auto-remove error after duration
    setTimeout(() => {
      this.clearErrorMessage(productItem);
    }, this.getHighlightDuration());
  }

  /**
   * Clear error message from a recommendation item.
   *
   * @param productItem - The recommendation product item element
   */
  protected clearErrorMessage(productItem: HTMLElement | null): void {
    if (!productItem) return;

    const errorElement = productItem.querySelector(this.constants.SELECTORS.ERROR_MESSAGE);
    if (errorElement) {
      errorElement.remove();
    }
  }

  /**
   * Extract a user-friendly error message from an error.
   * Override to customize error messages for shop-specific error patterns
   * or to add localization support.
   *
   * @param error - The error object (may be an Error instance or unknown type)
   * @returns User-friendly error message string
   *
   * @example
   * Override to add localized or shop-specific error messages:
   * ```typescript
   * protected getErrorMessage(error: unknown) {
   *   if (error instanceof Error && error.message.includes('limit')) {
   *     return 'Maximum cart limit reached for this item';
   *   }
   *   return super.getErrorMessage(error);
   * }
   * ```
   */
  protected getErrorMessage(error: unknown): string {
    if (error instanceof Error) {
      // Check for common Shopify cart errors
      if (error.message.includes('sold out') || error.message.includes('not available')) {
        return 'This item is sold out';
      }
      if (error.message.includes('quantity')) {
        return 'Unable to add requested quantity';
      }
      return error.message;
    }

    return 'Failed to add to cart. Please try again.';
  }

  /**
   * Called after a product is successfully added to cart from cart drawer.
   * Override to add custom post-add-to-cart logic such as analytics tracking,
   * notification display, or triggering shop-specific side effects.
   *
   * @param productId - The product ID that was added
   * @param variantId - The variant ID that was added
   *
   * @example
   * Override to track analytics after adding to cart:
   * ```javascript
   * window.boostWidgetIntegration.extend('CartDrawerRecommendationController', (Base) => {
   *   return class extends Base {
   *     async afterCartDrawerAddToCart(productId, variantId) {
   *       await super.afterCartDrawerAddToCart(productId, variantId);
   *       // Fire custom analytics event
   *       window.dataLayer?.push({
   *         event: 'rcu_add_to_cart',
   *         product_id: productId,
   *         variant_id: variantId,
   *       });
   *     }
   *   };
   * });
   * ```
   */
  protected async afterCartDrawerAddToCart(productId: string, variantId: string): Promise<void> {
    // Default: log the action
    this.appService.logger.debug('afterCartDrawerAddToCart hook', { productId, variantId });
  }

  /**
   * Full cleanup of all event listeners and resources.
   * Removes click/change handlers, cart drawer open/close listeners,
   * and destroys the native carousel.
   *
   * Call this when the module is being destroyed and the controller
   * should no longer respond to any events.
   */
  public destroy(): void {
    this.removeClickHandlers();
    this.destroyNativeCarousel();
    this.removeCartDrawerEventListeners();
  }

  /**
   * Whether the controller has been initialized with click handlers.
   *
   * @returns True if click handlers are currently active, false otherwise
   */
  public get hasInitialized(): boolean {
    return this.isInitialized;
  }

  // ==================== NATIVE CAROUSEL METHODS ====================
  // Uses native CSS/JS carousel instead of Slick for cart drawer

  /**
   * Get the CSS selector for the slider container.
   * Override this method to use a different slider selector.
   *
   * @returns CSS selector string for the slider container
   */
  protected getSliderSelector(): string {
    return `${this.constants.SELECTORS.RECOMMENDATION_CONTAINER} ${this.constants.SELECTORS.SLIDER}`;
  }

  /**
   * Get the CSS selector for the slider track (container of slides).
   *
   * @returns CSS selector string for the slider track
   */
  protected getSliderContainerSelector(): string {
    return this.constants.SELECTORS.SLIDER_CONTAINER;
  }

  /**
   * Get the CSS selector for individual slides.
   *
   * @returns CSS selector string for slides
   */
  protected getSlideSelector(): string {
    return this.constants.SELECTORS.SLIDE;
  }

  /**
   * Get the CSS selector for the previous button.
   *
   * @returns CSS selector string for the prev button
   */
  protected getPrevButtonSelector(): string {
    return this.constants.SELECTORS.PREV_BUTTON;
  }

  /**
   * Get the CSS selector for the next button.
   *
   * @returns CSS selector string for the next button
   */
  protected getNextButtonSelector(): string {
    return this.constants.SELECTORS.NEXT_BUTTON;
  }

  /**
   * Get the number of slides to show per view based on cart style.
   * Uses `numberOfProductPerRowSlideCart` for 'side' cart style,
   * and `numberOfProductPerRowPopupCart` for 'popup'/'modal' cart style.
   *
   * Override this method to customize slides per view calculation.
   *
   * @returns Number of slides to show per view
   *
   * @example
   * Override to use responsive slides per view based on viewport width:
   * ```typescript
   * protected getSlidesPerView() {
   *   if (window.innerWidth < 768) return 1;
   *   if (window.innerWidth < 1024) return 2;
   *   return super.getSlidesPerView();
   * }
   * ```
   */
  protected getSlidesPerView(): number {
    const cartSettings = this.appService.themeSettings.cart as {
      cartStyle?: string;
    };

    const recommendationContainer = document.querySelector(
      this.getRecommendationContainerSelector()
    );
    const widgetDesignSettings = this.getWidgetDesignSettings(recommendationContainer);

    const cartStyle = cartSettings?.cartStyle || 'side';

    // Determine slides per view based on cart style
    if (cartStyle === 'side' || cartStyle === 'slide') {
      const slidesPerRow = widgetDesignSettings?.numberOfProductPerRowSlideCart;
      return typeof slidesPerRow === 'number' ? slidesPerRow : 2;
    }

    // For popup/modal cart style
    const slidesPerRow = widgetDesignSettings?.numberOfProductPerRowPopupCart;
    return typeof slidesPerRow === 'number' ? slidesPerRow : 4;
  }

  /**
   * Extract widget design settings from the recommendation container data attributes
   * or from the TAE config (same approach as other recommendation widgets like volume-bundle).
   *
   * @param container - The recommendation container element (optional, for data attribute fallback)
   * @returns Widget design settings object, or null if no settings found
   *
   * @example
   * Override to merge additional shop-specific design settings:
   * ```typescript
   * protected getWidgetDesignSettings(container: Element | null) {
   *   const settings = super.getWidgetDesignSettings(container);
   *   return {
   *     ...settings,
   *     imageWidth: 400,
   *     numberOfProductPerRowSlideCart: 3,
   *   };
   * }
   * ```
   */
  protected getWidgetDesignSettings(container: Element | null): Record<string, unknown> | null {
    // Try to get settings from data attribute if available
    if (container) {
      const settingsAttr = container.getAttribute('data-widget-design-settings');
      if (settingsAttr) {
        try {
          return JSON.parse(settingsAttr);
        } catch {
          this.appService.logger.warn('Failed to parse widget design settings from data attribute');
        }
      }
    }

    // Get widget ID from cart settings (same approach as volume-bundle gets from context.app)
    const cartSettings = this.appService.themeSettings.cart as {
      widgetRecommendationId?: string;
    };
    const widgetId = cartSettings?.widgetRecommendationId;

    if (widgetId) {
      // Get settings from TAE config: recommendationWidgets['cart-drawer'][widgetId]
      // Same pattern as volume-bundle: context.app.recommendationWidgets['dynamic-bundle'][widgetId]
      const recommendationWidgets = this.appService.TAEAppConfig?.recommendationWidgets as Record<
        string,
        Record<string, { widgetDesignSettings?: Record<string, unknown> }>
      >;
      const widgetConfig = recommendationWidgets?.['cart-drawer']?.[widgetId];
      if (widgetConfig?.widgetDesignSettings) {
        return widgetConfig.widgetDesignSettings;
      }
    }

    return null;
  }

  /**
   * Initialize the native CSS/JS carousel for recommendation products.
   * Sets up slide dimensions, navigation buttons, and initial position.
   */
  protected initNativeCarousel(): void {
    // Clean up any existing carousel first to prevent memory leaks
    // (e.g., if cart drawer opened twice without closing)
    this.destroyNativeCarousel();

    const sliderElement = document.querySelector(this.getSliderSelector()) as HTMLElement;
    if (!sliderElement) {
      this.appService.logger.debug('Carousel slider element not found');
      return;
    }

    const containerElement = sliderElement.querySelector(
      this.getSliderContainerSelector()
    ) as HTMLElement;
    if (!containerElement) {
      this.appService.logger.debug('Carousel container element not found');
      return;
    }

    const slides = containerElement.querySelectorAll(this.getSlideSelector());
    const totalSlides = slides.length;

    if (totalSlides === 0) {
      this.appService.logger.debug('No slides found in carousel');
      return;
    }

    const slidesPerView = this.getSlidesPerView();

    // Calculate slide width based on container width and slides per view
    const containerWidth = sliderElement.clientWidth;
    const slideWidth = containerWidth / slidesPerView;

    // Set slide widths
    slides.forEach((slide) => {
      (slide as HTMLElement).style.flex = `0 0 ${slideWidth}px`;
      (slide as HTMLElement).style.maxWidth = `${slideWidth}px`;
    });

    // Initialize carousel state
    this.nativeCarouselState = {
      currentIndex: 0,
      totalSlides,
      slidesPerView,
      slideWidth,
      containerElement,
      sliderElement,
    };

    // Setup navigation buttons
    this.setupNativeCarouselNavigation(sliderElement);

    // Update button visibility
    this.updateNativeCarouselButtons();

    // Listen for window resize to recalculate slide dimensions
    this.setupResizeHandler();

    this.appService.logger.debug('Native carousel initialized', {
      totalSlides,
      slidesPerView,
      slideWidth,
    });
  }

  /**
   * Setup a debounced resize handler that reinitializes the carousel
   * when the window size changes (e.g., viewport rotation, drawer width change).
   */
  protected setupResizeHandler(): void {
    this.removeResizeHandler();

    let resizeTimer: ReturnType<typeof setTimeout>;
    this.boundResizeHandler = (): void => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        if (this.nativeCarouselState) {
          this.initNativeCarousel();
        }
      }, 150);
    };

    window.addEventListener('resize', this.boundResizeHandler);
  }

  /**
   * Remove the resize handler.
   */
  protected removeResizeHandler(): void {
    if (this.boundResizeHandler) {
      window.removeEventListener('resize', this.boundResizeHandler);
      this.boundResizeHandler = null;
    }
  }

  /**
   * Setup click handlers for carousel navigation buttons.
   *
   * @param sliderElement - The slider container element
   */
  protected setupNativeCarouselNavigation(sliderElement: HTMLElement): void {
    const prevButton = sliderElement.querySelector(this.getPrevButtonSelector());
    const nextButton = sliderElement.querySelector(this.getNextButtonSelector());

    if (prevButton) {
      prevButton.addEventListener('click', this.handleNativePrevClick);
    }

    if (nextButton) {
      nextButton.addEventListener('click', this.handleNativeNextClick);
    }

    // Setup drag/touch handlers
    this.setupNativeDragHandlers(sliderElement);
  }

  /**
   * Setup drag and touch handlers for carousel swipe functionality.
   *
   * @param sliderElement - The slider container element
   */
  protected setupNativeDragHandlers(sliderElement: HTMLElement): void {
    const containerElement = sliderElement.querySelector(
      this.getSliderContainerSelector()
    ) as HTMLElement;
    if (!containerElement) return;

    // Mouse events
    containerElement.addEventListener('mousedown', this.handleNativeDragStart);
    document.addEventListener('mousemove', this.handleNativeDragMove);
    document.addEventListener('mouseup', this.handleNativeDragEnd);

    // Touch events
    containerElement.addEventListener('touchstart', this.handleNativeTouchStart, { passive: true });
    containerElement.addEventListener('touchmove', this.handleNativeTouchMove, { passive: false });
    containerElement.addEventListener('touchend', this.handleNativeTouchEnd);

    // Prevent default drag behavior on images using event delegation
    this.boundDragStartHandler = (event: Event): void => {
      const target = event.target as HTMLElement;
      if (target.tagName === 'IMG') {
        event.preventDefault();
      }
    };
    containerElement.addEventListener('dragstart', this.boundDragStartHandler);

    // Add cursor style
    containerElement.style.cursor = 'grab';
  }

  /**
   * Handle mouse drag start.
   */
  protected handleNativeDragStart = (event: MouseEvent): void => {
    if (!this.nativeCarouselState?.containerElement) return;

    // Don't start drag if clicking on a button or link
    const target = event.target as HTMLElement;
    if (target.closest('button') || target.closest('a') || target.closest('select')) {
      return;
    }

    this.dragState.isDragging = true;
    this.dragState.startX = event.clientX;
    this.dragState.currentX = event.clientX;
    this.dragState.startTranslateX = this.getCurrentNativeTranslateX();

    this.nativeCarouselState.containerElement.style.transition = 'none';
    this.nativeCarouselState.containerElement.style.cursor = 'grabbing';
  };

  /**
   * Handle touch start.
   */
  protected handleNativeTouchStart = (event: TouchEvent): void => {
    if (!this.nativeCarouselState?.containerElement) return;

    const touch = event.touches[0];
    this.dragState.isDragging = true;
    this.dragState.startX = touch.clientX;
    this.dragState.currentX = touch.clientX;
    this.dragState.startTranslateX = this.getCurrentNativeTranslateX();

    this.nativeCarouselState.containerElement.style.transition = 'none';
  };

  /**
   * Handle mouse drag move.
   */
  protected handleNativeDragMove = (event: MouseEvent): void => {
    if (!this.dragState.isDragging || !this.nativeCarouselState?.containerElement) return;

    this.dragState.currentX = event.clientX;
    const deltaX = this.dragState.currentX - this.dragState.startX;
    const newTranslateX = this.dragState.startTranslateX + deltaX;

    // Apply boundaries
    const boundedTranslateX = this.getBoundedNativeTranslateX(newTranslateX);
    this.nativeCarouselState.containerElement.style.transform = `translateX(${boundedTranslateX}px)`;
  };

  /**
   * Handle touch move.
   */
  protected handleNativeTouchMove = (event: TouchEvent): void => {
    if (!this.dragState.isDragging || !this.nativeCarouselState?.containerElement) return;

    const touch = event.touches[0];
    this.dragState.currentX = touch.clientX;
    const deltaX = this.dragState.currentX - this.dragState.startX;

    // Prevent vertical scroll when swiping horizontally
    if (Math.abs(deltaX) > 10) {
      event.preventDefault();
    }

    const newTranslateX = this.dragState.startTranslateX + deltaX;

    // Apply boundaries
    const boundedTranslateX = this.getBoundedNativeTranslateX(newTranslateX);
    this.nativeCarouselState.containerElement.style.transform = `translateX(${boundedTranslateX}px)`;
  };

  /**
   * Handle mouse drag end.
   */
  protected handleNativeDragEnd = (): void => {
    if (!this.dragState.isDragging) return;

    this.finalizeNativeDrag();

    if (this.nativeCarouselState?.containerElement) {
      this.nativeCarouselState.containerElement.style.cursor = 'grab';
    }
  };

  /**
   * Handle touch end.
   */
  protected handleNativeTouchEnd = (): void => {
    if (!this.dragState.isDragging) return;

    this.finalizeNativeDrag();
  };

  /**
   * Finalize drag interaction and snap to nearest slide.
   */
  protected finalizeNativeDrag(): void {
    if (!this.nativeCarouselState) return;

    const deltaX = this.dragState.currentX - this.dragState.startX;
    const { slideWidth, totalSlides, slidesPerView } = this.nativeCarouselState;
    const threshold = slideWidth * 0.2; // 20% threshold to trigger slide change
    const maxIndex = Math.max(0, totalSlides - slidesPerView);

    // Determine direction and update index
    if (Math.abs(deltaX) > threshold) {
      if (deltaX < 0 && this.nativeCarouselState.currentIndex < maxIndex) {
        // Swiped left - go to next slide
        this.nativeCarouselState.currentIndex += 1;
      } else if (deltaX > 0 && this.nativeCarouselState.currentIndex > 0) {
        // Swiped right - go to previous slide
        this.nativeCarouselState.currentIndex -= 1;
      }
    }

    // Reset drag state
    this.dragState.isDragging = false;
    this.dragState.startX = 0;
    this.dragState.currentX = 0;
    this.dragState.startTranslateX = 0;

    // Animate to final position
    this.updateNativeCarouselPosition();
    this.updateNativeCarouselButtons();
  }

  /**
   * Get the current translateX value from the container element.
   *
   * @returns Current translateX value in pixels
   */
  protected getCurrentNativeTranslateX(): number {
    if (!this.nativeCarouselState?.containerElement) return 0;

    const transform = this.nativeCarouselState.containerElement.style.transform;
    const match = transform.match(/translateX\((-?[\d.]+)px\)/);
    return match ? parseFloat(match[1]) : 0;
  }

  /**
   * Get bounded translateX value within carousel limits.
   *
   * @param translateX - The desired translateX value
   * @returns Bounded translateX value
   */
  protected getBoundedNativeTranslateX(translateX: number): number {
    if (!this.nativeCarouselState) return translateX;

    const { totalSlides, slidesPerView, slideWidth } = this.nativeCarouselState;
    const maxTranslate = 0;
    const minTranslate = -((totalSlides - slidesPerView) * slideWidth);

    // Add some resistance at boundaries
    if (translateX > maxTranslate) {
      return maxTranslate + (translateX - maxTranslate) * 0.3;
    }
    if (translateX < minTranslate) {
      return minTranslate + (translateX - minTranslate) * 0.3;
    }

    return translateX;
  }

  /**
   * Handle click on previous button.
   * Slides by the number of products per row (slidesPerView).
   */
  protected handleNativePrevClick = (): void => {
    if (!this.nativeCarouselState) return;

    const { currentIndex, slidesPerView } = this.nativeCarouselState;

    if (currentIndex > 0) {
      // Slide by slidesPerView (products per row), but don't go below 0
      const newIndex = Math.max(0, currentIndex - slidesPerView);
      this.nativeCarouselState.currentIndex = newIndex;
      this.updateNativeCarouselPosition();
      this.updateNativeCarouselButtons();
    }
  };

  /**
   * Handle click on next button.
   * Slides by the number of products per row (slidesPerView).
   */
  protected handleNativeNextClick = (): void => {
    if (!this.nativeCarouselState) return;

    const { currentIndex, totalSlides, slidesPerView } = this.nativeCarouselState;
    const maxIndex = Math.max(0, totalSlides - slidesPerView);

    if (currentIndex < maxIndex) {
      // Slide by slidesPerView (products per row), but don't exceed maxIndex
      const newIndex = Math.min(maxIndex, currentIndex + slidesPerView);
      this.nativeCarouselState.currentIndex = newIndex;
      this.updateNativeCarouselPosition();
      this.updateNativeCarouselButtons();
    }
  };

  /**
   * Update the carousel position by translating the container.
   */
  protected updateNativeCarouselPosition(): void {
    if (!this.nativeCarouselState || !this.nativeCarouselState.containerElement) return;

    const { currentIndex, slideWidth } = this.nativeCarouselState;
    const translateX = -(currentIndex * slideWidth);

    this.nativeCarouselState.containerElement.style.transform = `translateX(${translateX}px)`;
    this.nativeCarouselState.containerElement.style.transition = 'transform 0.3s ease-in-out';
  }

  /**
   * Update carousel navigation button visibility/state.
   * Hides prev button at start, hides next button at end.
   */
  protected updateNativeCarouselButtons(): void {
    if (!this.nativeCarouselState || !this.nativeCarouselState.sliderElement) return;

    const { currentIndex, totalSlides, slidesPerView, sliderElement } = this.nativeCarouselState;
    const maxIndex = Math.max(0, totalSlides - slidesPerView);

    const prevButton = sliderElement.querySelector(this.getPrevButtonSelector()) as HTMLElement;
    const nextButton = sliderElement.querySelector(this.getNextButtonSelector()) as HTMLElement;

    // Hide prev button if at start
    if (prevButton) {
      prevButton.style.display = currentIndex <= 0 ? 'none' : '';
    }

    // Hide next button if at end
    if (nextButton) {
      nextButton.style.display = currentIndex >= maxIndex ? 'none' : '';
    }
  }

  /**
   * Destroy the native carousel and clean up resources.
   */
  protected destroyNativeCarousel(): void {
    if (!this.nativeCarouselState) return;

    const { sliderElement, containerElement } = this.nativeCarouselState;

    if (sliderElement) {
      const prevButton = sliderElement.querySelector(this.getPrevButtonSelector());
      const nextButton = sliderElement.querySelector(this.getNextButtonSelector());

      if (prevButton) {
        prevButton.removeEventListener('click', this.handleNativePrevClick);
      }

      if (nextButton) {
        nextButton.removeEventListener('click', this.handleNativeNextClick);
      }
    }

    // Remove drag event listeners
    if (containerElement) {
      containerElement.removeEventListener('mousedown', this.handleNativeDragStart);
      containerElement.removeEventListener('touchstart', this.handleNativeTouchStart);
      containerElement.removeEventListener('touchmove', this.handleNativeTouchMove);
      containerElement.removeEventListener('touchend', this.handleNativeTouchEnd);

      if (this.boundDragStartHandler) {
        containerElement.removeEventListener('dragstart', this.boundDragStartHandler);
        this.boundDragStartHandler = null;
      }
    }

    // Remove document-level listeners
    document.removeEventListener('mousemove', this.handleNativeDragMove);
    document.removeEventListener('mouseup', this.handleNativeDragEnd);

    // Remove resize handler
    this.removeResizeHandler();

    // Reset drag state
    this.dragState = {
      isDragging: false,
      startX: 0,
      currentX: 0,
      startTranslateX: 0,
    };

    this.nativeCarouselState = null;
  }
}
